import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Typography, CircularProgress, Box,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { NotificationManager } from "react-notifications";
import {
  GetOperatorAccounts, AddOperatorAccount, RemoveOperatorAccount,
} from "../../../services/ClientsMaintenanceService";
import UserSelectorDialog from "../../common/UserSelectorDialog";

const OperatorAssignmentDialog = ({ open, onClose, accountNumber }) => {
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userSelectorOpen, setUserSelectorOpen] = useState(false);

  useEffect(() => {
    if (open && accountNumber) loadAssigned();
  }, [open, accountNumber]);

  const loadAssigned = async () => {
    try {
      setLoading(true);
      const res = await GetOperatorAccounts(accountNumber);
      setAssigned(res.results || []);
    } catch { setAssigned([]); }
    finally { setLoading(false); }
  };

  const handleUserSelect = async (user) => {
    setUserSelectorOpen(false);
    if (!user) return;
    try {
      setLoading(true);
      const result = await AddOperatorAccount({ accountNumber, operator: user.operator });
      if (!result.results) {
        NotificationManager.warning("Operator already assigned.");
      } else {
        await loadAssigned();
      }
    } catch { NotificationManager.error("Failed to assign operator."); }
    finally { setLoading(false); }
  };

  const handleRemove = async (recordid) => {
    try {
      setLoading(true);
      await RemoveOperatorAccount(recordid);
      await loadAssigned();
    } catch { NotificationManager.error("Failed to remove operator."); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Operator Assignment — Account {accountNumber}</DialogTitle>
        <DialogContent dividers>
          {loading && assigned.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Operator</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assigned.length === 0 && (
                    <TableRow><TableCell colSpan={3}><Typography color="text.secondary" variant="body2">No operators assigned.</Typography></TableCell></TableRow>
                  )}
                  {assigned.map((row) => (
                    <TableRow key={row.recordid} hover>
                      <TableCell>{row.operator}</TableCell>
                      <TableCell>{row.operatorName}</TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => handleRemove(row.recordid)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" size="small" onClick={() => setUserSelectorOpen(true)} disabled={loading}>
            Add Operator
          </Button>
          <Button onClick={onClose} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      <UserSelectorDialog
        open={userSelectorOpen}
        onClose={() => setUserSelectorOpen(false)}
        onSelect={handleUserSelect}
        multiSelect={false}
      />
    </>
  );
};

export default OperatorAssignmentDialog;
